import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser';
import { shouldExcludeFromCio } from '@brands/hooks/shouldExcludeFromCio';
import { ClientCustomerIoRouting } from '@brands/services/identity/getClientCustomerIo';
import { UserProfile } from '@brands/services/identity/types/UserProfile';

export interface CustomerIoAnalytics {
  identify: (id: string, traits: Record<string, unknown>) => void;
  page: (properties: Record<string, unknown>) => void;
  reset: () => void;
}

export function customerIoWriteKey(user: UserProfile, defaultKey?: string): string | null {
  if (shouldExcludeFromCio(user) || !Array.isArray(user.clients)) return null;
  const exclusive = user.clients.filter((tag) => tag.customer_io_exclusive);
  if (exclusive.length > 1) return null;
  if (exclusive.length === 1) {
    return exclusive[0].disable_customerio ? null : exclusive[0].customer_io_browser_key || null;
  }
  return defaultKey || null;
}

// Only the public landing/sign-up routes can carry a client code. Explicit
// static routes must not be mistaken for clients by the /:clientCode route.
const STATIC_ROUTES = new Set([
  'sign-in',
  'sign-up',
  'existing-account',
  'complete-signup',
  'terms-of-service',
  'notice-of-privacy-practices',
  'washington_my_health_my_data_privacy_notice',
  'consent-policy',
  'privacy-policy',
  'affiliated-practice-list',
  'smile-scan-disclaimer',
  'zendesk_logout',
  'dashboard',
  'my-profile',
  'my-care',
  'my-care-journey',
  'patient',
  'provider',
  'careadvisor',
  'collector',
  'admin',
  'superadmin',
  'clientadmin',
]);

export function customerIoClientCode(pathname: string, search: string): string | null {
  const query = new URLSearchParams(search).get('client');
  if (query) return query;
  const [first, second] = pathname.split('/').filter(Boolean);
  if (!first || STATIC_ROUTES.has(first.toLowerCase())) return null;
  return !second || second === 'sign-up' ? decodeURIComponent(first) : null;
}

function isAuthenticationPage(pathname: string): boolean {
  return (
    pathname === '/' ||
    /^\/(sign-in|sign-up|existing-account|complete-signup)(\/|$)/.test(pathname) ||
    pathname === '/kare-mobile/login' ||
    customerIoClientCode(pathname, '') !== null
  );
}

type TrackingLocation = { pathname: string; search: string; origin: string };
type TrackingDependencies = {
  getToken: () => string;
  getProfile: () => Promise<UserProfile>;
  getClientRouting: (code: string) => Promise<ClientCustomerIoRouting>;
  getLocation: () => TrackingLocation;
  analytics: (key: string) => Promise<CustomerIoAnalytics>;
  defaultKey?: string;
};

/** Owns routing and identity together: no auth flow can identify into an old SDK. */
export function createCustomerIoTracker(deps: TrackingDependencies) {
  let generation = 0;
  let profileToken: string | null = null;
  let profilePromise: Promise<UserProfile> | null = null;
  let active: CustomerIoAnalytics | null = null;
  let activeKey: string | null = null;
  let activeIdentity: string | null = null;

  const reset = () => {
    active?.reset();
    active = null;
    activeKey = null;
    activeIdentity = null;
  };

  const page = async (): Promise<void> => {
    const request = ++generation;
    const token = deps.getToken();
    const location = deps.getLocation();
    try {
      // Auth flows set cookies before finishing invitations/SSO and navigating.
      // Do not let a workspace reload interrupt those steps or a mobile handoff.
      if (new URLSearchParams(location.search).has('auth') || (token && isAuthenticationPage(location.pathname))) {
        reset();
        return;
      }
      let user: UserProfile | null = null;
      let key: string | null = null;
      if (token) {
        if (profileToken !== token || !profilePromise) {
          // Redux persists profiles across reloads; its old client tags cannot
          // authorize delivery. Resolve the current session from the API first.
          reset();
          profileToken = token;
          profilePromise = deps.getProfile();
        }
        user = await profilePromise;
        key = customerIoWriteKey(user, deps.defaultKey);
      } else {
        profileToken = null;
        profilePromise = null;
        reset();
        const code = customerIoClientCode(location.pathname, location.search);
        if (code) {
          const routing = await deps.getClientRouting(code);
          key = routing.enabled ? (routing.exclusive ? routing.browser_write_key : deps.defaultKey || null) : null;
        } else {
          key = deps.defaultKey || null;
        }
      }
      // A late profile/config response must never revive a previous session.
      if (request !== generation || token !== deps.getToken()) return;
      if (!key) {
        reset();
        return;
      }
      // Never enqueue identity/page calls in the SDK's pre-initialization
      // buffer: it groups calls by method, so a logout reset can run before
      // an older identify. Recheck the session after the SDK is ready.
      const analytics = await deps.analytics(key);
      if (request !== generation || token !== deps.getToken()) return;
      const identity = user ? String(user.id) : null;
      if (activeKey !== key || activeIdentity !== identity) {
        reset();
        active = analytics;
        activeKey = key;
        activeIdentity = identity;
        active.reset();
      }
      if (user) {
        active!.identify(String(user.id), {
          email: user.email,
          created_at: user.created_at ? Math.floor(new Date(user.created_at).getTime() / 1000) : undefined,
          first_name: user.basic_info?.first_name || '',
          last_name: user.basic_info?.last_name || '',
        });
      }
      // Exclude query strings from the explicit page properties.
      active!.page({ path: location.pathname, url: location.origin + location.pathname });
    } catch {
      if (request === generation) {
        profilePromise = null;
        reset();
      }
      // Failed routing is not permission to send to the default workspace.
    }
  };

  return {
    page,
    invalidateProfile: () => {
      ++generation;
      profilePromise = null;
      reset();
    },
    stop: () => {
      ++generation;
      reset();
    },
  };
}

let workspace: { key: string; ready: Promise<CustomerIoAnalytics> } | null = null;
let reloading = false;

/** The SDK's Gist singleton and its listeners belong to one workspace per document. */
export function customerIoAnalytics(key: string): Promise<CustomerIoAnalytics> {
  if (reloading || (workspace && workspace.key !== key)) {
    // reset() clears identity, but neither changes Gist's siteId nor removes
    // its listeners. A fresh document is required before loading another key.
    if (!reloading) {
      reloading = true;
      window.location.reload();
    }
    return Promise.reject(new Error('Customer.io workspace change requires a fresh document'));
  }
  if (workspace) return workspace.ready;
  const ready = AnalyticsBrowser.load(
    { writeKey: key },
    {
      // Shared SDK cookies can import another workspace's identity and queued
      // events. Identity is supplied explicitly on every SPA page instead.
      disableClientPersistence: true,
      initialPageview: false,
      // Do not consume a snippet queue belonging to another analytics library.
      globalAnalyticsKey: '__dentistryOneCustomerIo',
    },
  ).then(
    ([sdk]): CustomerIoAnalytics => ({
      // Use the initialized instance directly, bypassing its pre-init buffer.
      identify: (id, traits) => {
        void sdk.identify(id, traits).catch(() => undefined);
      },
      page: (properties) => {
        void sdk.page(properties).catch(() => undefined);
      },
      reset: () => sdk.reset(),
    }),
  );
  workspace = { key, ready };
  return ready;
}
